import * as Hooks from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { useSelector } from '../redux';
import { AppError } from '../../../data/appError';
import { Product } from '../../../data/product';
import { ProductSetLayout } from '../../../data/productSetLayout';
import * as CoreStore from '../../../store/core';
import * as Logging from '../../../store/actions/logging';
import * as Selectors from '../../../store/selectors';
import { useGetLayouts } from '../byTenant/useGetLayouts';
import * as Pr from '../../../data/product';

function isSkuData(
    launchData: { sku: string } | { layoutId: string },
): launchData is { sku: string } {
    return (launchData as { sku: string }).sku !== undefined;
}

/** Parse launch data
 *
 * If launch data is for a product sku, attempt to set selectedProduct
 * If launch data is for a product set sku, attempt to set selectedProductSet and
 * selectedProductSetLayout
 * If launch data is for a saved layout, attempt to set layoutId
 *
 * If any error situations are detected, set nothing and dispatch an error state
 */
export const useLaunchData = () => {
    const launchData = useSelector(Selectors.Core.launchData);
    const products = useSelector(Selectors.ProductData.products);
    const [selectedProductSet, setSelectedProductSet] = Hooks.useState<Product | null>(null);
    const [selectedProduct, setSelectedProduct] = Hooks.useState<Product | null>(null);
    const [selectedProductSetLayout, setSelectedProductSetLayout] =
        Hooks.useState<ProductSetLayout | null>(null);
    const [layoutId, setLayoutId] = Hooks.useState<string | null>(null);
    const layouts = useGetLayouts();

    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        if (launchData && isSkuData(launchData)) {
            if (products && layouts) {
                const product = products?.find(p => Pr.productId(p) === launchData.sku);

                if (product) {
                    if (product.kind === 'simple') {
                        setSelectedProduct(product);
                    } else {
                        const productSetLayout = layouts.find(l => l.productSet === launchData.sku);
                        if (productSetLayout) {
                            setSelectedProductSet(product);
                            setSelectedProductSetLayout(productSetLayout);
                        } else {
                            dispatch(
                                Logging.logError(
                                    'Space launched with product set ID for which no corresponding layout can be found',
                                    {
                                        spaceLaunchDataProductId: launchData.sku,
                                    },
                                ),
                            );
                            dispatch(CoreStore.error(AppError.LAYOUT_LOAD_FAILED));
                        }
                    }
                } else {
                    dispatch(
                        Logging.logError(
                            'Space launched with product ID for which no corresponding product can be found',
                            {
                                spaceLaunchDataProductId: launchData.sku,
                            },
                        ),
                    );
                    dispatch(CoreStore.error(AppError.LAYOUT_LOAD_FAILED));
                }
            } else {
                // products/layouts have not yet loaded, do nothing
            }
        } else if (launchData && !isSkuData(launchData)) {
            setLayoutId(launchData.layoutId);
        }
    }, [launchData, products, layouts]);

    const clearData = () => {
        setSelectedProduct(null);
        setSelectedProductSet(null);
        setSelectedProductSetLayout(null);
        setLayoutId(null);
    };

    return { selectedProduct, selectedProductSet, selectedProductSetLayout, layoutId, clearData };
};
